/** Decrypt sellers' payout account numbers in the browser.
 *
 * The marketplace encrypts each number with the owner's RSA public key
 * (RSA-OAEP, SHA-256). The owner's private key is pasted here, imported as a
 * non-extractable CryptoKey and kept only in memory: it is never stored or
 * sent anywhere, so a server breach can't read the numbers. */

export class PayoutKeyError extends Error {}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64.replace(/\s+/g, ""));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

const subtle = () => {
  const s = globalThis.crypto?.subtle;
  if (!s) throw new PayoutKeyError("This browser can't decrypt here. Open the dashboard over https in a current browser.");
  return s;
};

/** Accepts the PEM from `openssl genpkey` (BEGIN PRIVATE KEY, PKCS#8). */
export async function importPrivateKey(pem: string): Promise<CryptoKey> {
  const text = pem.trim();
  if (/BEGIN RSA PRIVATE KEY/.test(text)) {
    throw new PayoutKeyError("This is an old-format key. Convert it with: openssl pkcs8 -topk8 -nocrypt -in key.pem");
  }
  if (/BEGIN (PUBLIC KEY|ENCRYPTED PRIVATE KEY)/.test(text)) {
    throw new PayoutKeyError(/PUBLIC/.test(text) ? "That's the public key. Paste the private one." : "This key has a password. Use the unencrypted private key.");
  }
  const body = text.replace(/-----BEGIN PRIVATE KEY-----|-----END PRIVATE KEY-----/g, "").replace(/\s+/g, "");
  if (!body) throw new PayoutKeyError("Paste your payout key (it starts with -----BEGIN PRIVATE KEY-----).");
  let der: Uint8Array;
  try {
    der = base64ToBytes(body);
  } catch {
    throw new PayoutKeyError("That doesn't look like a key. Paste the whole file, including the BEGIN and END lines.");
  }
  try {
    return await subtle().importKey("pkcs8", der, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["decrypt"]);
  } catch (err) {
    if (err instanceof PayoutKeyError) throw err;
    throw new PayoutKeyError("That isn't a valid RSA private key.");
  }
}

/** One account number from its base64 ciphertext. Throws when the key doesn't match. */
export async function decryptAccount(key: CryptoKey, ciphertext: string): Promise<string> {
  try {
    const plain = await subtle().decrypt({ name: "RSA-OAEP" }, key, base64ToBytes(ciphertext));
    return new TextDecoder().decode(plain);
  } catch {
    throw new PayoutKeyError("This key doesn't unlock these payout details. Check you pasted the right key.");
  }
}
